/**
 * Timezone-safe date helpers (web).
 * Backend timestamps are UTC; some are serialized without a 'Z' suffix, which
 * JS would otherwise parse as LOCAL time (causing "future" / negative "x ago").
 * parseUTC normalizes that. Absolute times render in the user's chosen tz.
 */

export function parseUTC(ts) {
  if (ts == null) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts === "number") return new Date(ts);
  const s = String(ts).trim();
  if (!s) return null;
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(s);
  const iso = hasTz ? s : `${s.replace(" ", "T")}Z`;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function fromNow(ts) {
  const d = parseUTC(ts);
  if (!d) return "";
  let diff = Date.now() - d.getTime();
  if (diff < 0) diff = 0;
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}

const DEFAULT_TZ = "America/New_York";

export const DEFAULT_DISPLAY_TZ = "America/New_York";

export function formatDateTime(ts, timezone) {
  const d = parseUTC(ts);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || DEFAULT_TZ,
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

export function formatDate(ts, timezone) {
  const d = parseUTC(ts);
  if (!d) return "";
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: timezone || DEFAULT_TZ,
      month: "short", day: "numeric", year: "numeric",
    }).format(d);
  } catch {
    return d.toLocaleDateString();
  }
}

export function deviceTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TZ;
  } catch {
    return DEFAULT_TZ;
  }
}

/** Curated timezones for the settings picker, labelled by zone name (not city). */
export const TIMEZONE_OPTIONS = [
  { value: "UTC", label: "UTC" },
  { value: "America/Los_Angeles", label: "Americas: Pacific Time" },
  { value: "America/Denver", label: "Americas: Mountain Time" },
  { value: "America/Chicago", label: "Americas: Central Time" },
  { value: "America/New_York", label: "Americas: Eastern Time" },
  { value: "America/Halifax", label: "Americas: Atlantic (Halifax time)" },
  { value: "America/Sao_Paulo", label: "Americas: Brasília Time" },
  { value: "America/Argentina/Buenos_Aires", label: "Americas: Argentina Time" },
  { value: "Europe/London", label: "Europe: UK / Greenwich Time" },
  { value: "Europe/Paris", label: "Europe: Central European Time" },
  { value: "Europe/Athens", label: "Europe: Eastern European Time" },
  { value: "Europe/Moscow", label: "Europe: Moscow Time" },
  { value: "Asia/Dubai", label: "Asia: Gulf Time" },
  { value: "Asia/Kolkata", label: "Asia: India Time" },
  { value: "Asia/Singapore", label: "Asia: Singapore Time" },
  { value: "Asia/Tokyo", label: "Asia: Japan Time" },
  { value: "Australia/Sydney", label: "Australia: Eastern Time" },
  { value: "Pacific/Auckland", label: "Pacific: New Zealand Time" },
];

const TZ_LABELS = Object.fromEntries(TIMEZONE_OPTIONS.map((o) => [o.value, o.label]));

/** Friendly label for an IANA timezone value. */
export function tzLabel(value) {
  return TZ_LABELS[value] || (value || "").replace(/_/g, " ");
}

/** Backwards-compatible flat list of IANA ids. */
export const COMMON_TIMEZONES = TIMEZONE_OPTIONS.map((o) => o.value);
